import { useCallback, useEffect, useRef, useState } from 'react'

import {
	getIsMobileViewport,
	getSlideDirection,
	REFERENCES_MOBILE_MEDIA_QUERY,
	type ReferenceSlideDirection
} from '../components/a-references-swiper.utils'

import type { Swiper as SwiperType } from 'swiper'


export const useReferencesSwiper = (referenceCount: number) => {
	const currentSlideIndexRef = useRef(0)
	const activeIndexRef = useRef(0)
	const enteringIndexRef = useRef<number | null>(null)
	const textLeftSwiperRef = useRef<SwiperType | null>(null)
	const [isMobile, setIsMobile] = useState(getIsMobileViewport)
	const [activeIndex, setActiveIndex] = useState(0)
	const [enteringIndex, setEnteringIndex] = useState<number | null>(null)
	const [slideDirection, setSlideDirection] = useState<ReferenceSlideDirection>('next')

	const enableLoop = referenceCount >= 3
	const textSwiperSpeed = isMobile ? 500 : 450

	useEffect(() => {
		const mediaQuery = window.matchMedia(REFERENCES_MOBILE_MEDIA_QUERY)
		const updateIsMobile = () => setIsMobile(mediaQuery.matches)

		updateIsMobile()
		mediaQuery.addEventListener('change', updateIsMobile)

		return () => mediaQuery.removeEventListener('change', updateIsMobile)
	}, [])

	const syncLeftTextSwiper = useCallback(
		(index: number) => {
			const leftSwiper = textLeftSwiperRef.current
			if (!leftSwiper) return

			if (enableLoop) {
				leftSwiper.slideToLoop(index, textSwiperSpeed, false)
			} else {
				leftSwiper.slideTo(index, textSwiperSpeed, false)
			}
		},
		[enableLoop, textSwiperSpeed]
	)

	const finishEntering = useCallback(() => {
		if (enteringIndexRef.current === null) return

		activeIndexRef.current = enteringIndexRef.current
		setActiveIndex(enteringIndexRef.current)
		enteringIndexRef.current = null
		setEnteringIndex(null)
	}, [])

	const goToPhoto = useCallback(
		(newIndex: number) => {
			if (enteringIndexRef.current !== null) {
				activeIndexRef.current = enteringIndexRef.current
				setActiveIndex(enteringIndexRef.current)
				enteringIndexRef.current = null
				setEnteringIndex(null)
			}

			if (newIndex === activeIndexRef.current) return

			const direction = getSlideDirection(
				activeIndexRef.current,
				newIndex,
				referenceCount,
				enableLoop
			)

			enteringIndexRef.current = newIndex
			setSlideDirection(direction)
			setEnteringIndex(newIndex)
		},
		[enableLoop, referenceCount]
	)

	const handleSlideChange = useCallback(
		(swiper: SwiperType) => {
			const newIndex = swiper.realIndex

			if (newIndex === currentSlideIndexRef.current) return

			const direction = getSlideDirection(
				currentSlideIndexRef.current,
				newIndex,
				referenceCount,
				enableLoop
			)

			currentSlideIndexRef.current = newIndex
			setSlideDirection(direction)
			syncLeftTextSwiper(newIndex)
			goToPhoto(newIndex)

			if (typeof window !== 'undefined' && window.resetAllTruncateContent) {
				window.resetAllTruncateContent()
			}
		},
		[enableLoop, goToPhoto, referenceCount, syncLeftTextSwiper]
	)

	return {
		activeIndex,
		enableLoop,
		enteringIndex,
		finishEntering,
		handleSlideChange,
		slideDirection,
		textLeftSwiperRef,
		textSwiperSpeed
	}
}
